import { UnstyledButton } from '@mantine/core';
import { useGetSimilarDebtors } from 'api/index';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import CustomTableViews from 'shared/components/CustomTable/CustomTableViews';
import { validatePairedAmountFilters } from 'shared/components/CustomTable/filters/pairedAmountFilters';
import { Icon, IconName } from 'shared/components/Icon';
import { queryParamParser } from 'shared/utils/view';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import type { Debtor } from 'types/store/client-state';

import { NoCheckBox } from './BatchActions/NoCheckBox';
import { ShowCheckBox } from './BatchActions/ShowCheckBox';
import styleIdentifiers from './ClientsList.module.scss';
import { ExportDebtorCsv } from './ExportDebtorCsv';
import { type GetDebtorsViewParams, useGetDebtorsView } from './useGetDebtorsView';

const styles = classNames.bind(styleIdentifiers);

// Builds the view-data query params from the URL: view id from the hash, filters
// from the search (parsed the same way the legacy getView saga did).
const paramsFromLocation = (hash: string, search: string): GetDebtorsViewParams => ({
  view_id: hash.slice(1),
  ...queryParamParser(qs.parse(search, { arrayFormat: 'comma' })),
});

export const ClientsList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [params, setParams] = useState<GetDebtorsViewParams>(() =>
    paramsFromLocation(location.hash, location.search),
  );
  const { debtors, isDebtorsInitialLoading, refetchDebtors } = useGetDebtorsView(params);

  const [selectedClients, setSelectedClients] = useState<Debtor[]>([]);
  const [areAllItemsAcrossPagesSelected, setAreAllItemsAcrossPagesSelected] = useState(false);

  const { data: similarDebtorsListResponse, refetch: refetchSimilarDebtors } = useGetSimilarDebtors(
    { query: { enabled: false } },
  );

  const shouldShowBatchActions = selectedClients.length > 0;

  // Re-sync the query params whenever the URL changes (the orchestrator writes
  // filters/sort/pagination into the URL via history.push).
  useEffect(() => {
    setParams(paramsFromLocation(location.hash, location.search));
  }, [location]);

  // Refresh the duplicates count once the list has loaded (was keyed on the saga
  // `loaded` flag; now on the React Query load completing).
  useEffect(() => {
    if (!isDebtorsInitialLoading) refetchSimilarDebtors();
  }, [isDebtorsInitialLoading, refetchSimilarDebtors]);

  const handleFilters = (filters: any) =>
    validatePairedAmountFilters([
      'payment_delay',
      'remaining_balance',
      'annual_ca',
      'overdue_amount',
    ])({ ...filters });

  const exportCsv = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(i18nKeys.EXPORT_CSV),
      children: <ExportDebtorCsv />,
    });
  };

  const showDebtor = (item: any, e: React.MouseEvent<HTMLTableRowElement>) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      window.open(`/clients/${item.id}`, '_blank');
      return;
    }

    history.push(`/clients/${item.id}`);
  };

  const handleEditClient = (e: React.MouseEvent<HTMLButtonElement>, client: unknown) => {
    e.stopPropagation();
    if (!(typeof client === 'object' && client !== null && 'id' in client)) return;
    history.push(`/clients/${client.id}/edit`);
  };

  const actionsCol = (client: unknown) => (
    <UnstyledButton
      display="block"
      aria-label={t(i18nKeys.EDIT)}
      onClick={e => handleEditClient(e, client)}
    >
      <Icon name={IconName.PENCIL} size="18px" />
    </UnstyledButton>
  );

  const getActions = () => {
    if (shouldShowBatchActions)
      return (
        <ShowCheckBox
          listUserChecked={selectedClients}
          selectAll={areAllItemsAcrossPagesSelected}
          totalObjects={debtors?.metadata?.pagination.total_objects ?? 0}
          reload={refetchDebtors}
        />
      );

    return (
      <NoCheckBox
        exportCSV={exportCsv}
        duplicatesCount={similarDebtorsListResponse?.metadata?.pagination.total_objects ?? 0}
      />
    );
  };

  return (
    <CustomTableViews
      className={styles('client-list')}
      title={t(i18nKeys.CLIENTS)}
      tableName="debtors"
      onClickRow={showDebtor}
      actionsCol={actionsCol}
      actions={getActions()}
      callbackAction={refetchDebtors}
      isLoading={isDebtorsInitialLoading}
      handleFilters={handleFilters}
      pagination={debtors?.metadata?.pagination}
      items={debtors?.data ?? []}
      onChangeSelectionCallback={values => setSelectedClients(values as Array<Debtor>)}
      onChangeSelectItemsAcrossAllPagesCallback={setAreAllItemsAcrossPagesSelected}
    />
  );
};
